import { useRouter } from 'expo-router'
import { useEffect, useState, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'
import { ScrollView } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Chip } from '@/src/elements/chip'
import { Image } from '@/src/elements/image'
import { GradientTypography } from '@/src/elements/text/gradient'
import { Typography } from '@/src/elements/text/Typography'
import {
  GENERAL_NOTIFICATION_SETTINGS,
  PUSH_EVENT_LABEL,
} from '@/src/features/notification/models/notification.constants'
import { useNotificationSettingsMutation } from '@/src/features/notification/services/useNotificationSettingsMutation'
import { PushRequest } from '@/src/features/notification/ui/components/PushRequest/PushRequest'
import { useXMRPMRedirect } from '@/src/features/rosario/services/useXMRPMRedirect'
import { ErrorMessage } from '@/src/general/components/ErrorMessage'
import { useToast } from '@/src/general/components/ToastContext'
import { GTM } from '@/src/general/utils/gtm'
import { EVENTS } from '@/src/general/utils/gtm/constants'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

export const WatchmanPushRequestScreen = (): ReactNode => {
  const [isPermissionGranted, setIsPermissionGranted] = useState(false)

  const { dismissTo, navigate } = useRouter()
  const { routes } = useRoutes()
  const { t } = useTranslate()
  const { addToast } = useToast()

  const finishRedirect = useXMRPMRedirect({ destination: routes['prayer-space']() })

  const { mutate, isPending, error, isSuccess } = useNotificationSettingsMutation(
    GENERAL_NOTIFICATION_SETTINGS.ON_INTENTION_WATCHMAN,
  )

  useEffect(() => GTM.sendEvent(EVENTS.WATCHMAN_ASK_PUSH_DISPLAYED), [])

  useEffect(() => {
    if (isPermissionGranted && isSuccess) {
      addToast({
        message: t('push.activated'),
        colors: 'success',
      })
      dismissTo(finishRedirect)
    }
  }, [isPermissionGranted, isSuccess, addToast, dismissTo, finishRedirect, t])

  const handlePermissionChanged = (permission: NotificationPermission | null) => {
    if (permission === 'granted') {
      setIsPermissionGranted(true)
    }
  }

  return (
    <ScrollView
      className="bg-primary-bg-light"
      contentContainerClassName="web:items-center h-full web:min-h-screen native:min-h-full justify-between pt-4 pb-12"
    >
      <View className="w-full grow max-w-md gap-6 p-6 justify-around">
        <Chip colors="primary" variant="flat" text={t('word.watchman')} className="self-center" />
        <GradientTypography black size="2xl" colors="primary">
          <FormattedMessage id="watchman.push.title" values={{ br: () => <br /> }} />
        </GradientTypography>
        <View className="h-64 w-full">
          <Image source={require('@/img/watchman/watchman.png')} radius="none" contentFit="contain" />
        </View>
        <View className="gap-6">
          <Typography>
            <FormattedMessage
              id="watchman.push.description"
              values={{ strong: (chunks) => <strong>{chunks}</strong> }}
            />
          </Typography>
          <Typography>
            <FormattedMessage id="watchman.push.details" />
          </Typography>
        </View>
      </View>
      <View className="w-full max-w-md gap-4 p-6">
        <PushRequest eventLabel={PUSH_EVENT_LABEL.PRAYER_WATCHMAN} onPermissionChanged={handlePermissionChanged}>
          {({ isLoading, onTrigger }) => (
            <Button
              fullWidth
              isLoading={isPending || isLoading}
              onPress={() => {
                GTM.sendEvent(EVENTS.WATCHMAN_ASK_PUSH_CLICKED)
                mutate({ push: true })
                onTrigger()
              }}
              variant="solid"
              colors="primary"
            >
              <FormattedMessage id="community.notifications.activate-push" />
            </Button>
          )}
        </PushRequest>
        {error && <ErrorMessage error={error} defaultErrorKey="app.error" />}
        <Button
          fullWidth
          onPress={() => {
            GTM.sendEvent(EVENTS.WATCHMAN_ASK_PUSH_REFUSED)
            navigate(finishRedirect)
          }}
          variant="faded"
          colors="primary"
        >
          <FormattedMessage id="words.maybe-later" />
        </Button>
      </View>
    </ScrollView>
  )
}
